import { useReducer } from 'react';

import { useAssetActionModalStore } from '@/store/asset-action-modal.store';
import { selectOrderType } from '@repo/api-adapters/selectors/customer-assets.selectors';
import { translate } from '@repo/phrase';
import { isActionKey } from '@repo/utils';

import { QueryError } from '@/components/query-error';
import { QueryFetchingIndicator } from '@/components/query-fetching-indicator/query-fetching-indicator.component';
import { AssetContextDropdown } from '@/components/status-header/asset-context-buttons/asset-context-dropdown';
import { AssetContextDropdownModal } from '@/components/status-header/asset-context-buttons/asset-context-dropdown-modal';

import { useAsset, useAssetParams } from '@/hooks/asset.hooks';
import { useResponsive } from '@/hooks/responsive.hooks';

import type { ActionModalProps } from '../../modals/asset-modal.types';
import { ACTION_BUTTONS_CONFIG, ASSET_ACTIONS_CONFIG, ASSET_ACTION_MODALS_CONFIG } from '../status-header.config';
import { AssetCloseButton } from './asset-close-button';
import { getComponentConfig } from './asset-context-buttons.utils';
import { AssetToggleDropdownButton } from './asset-toggle-dropdown-button';

import styles from './styles.module.scss';

export const AssetContextButtons = () => {
    const { assetType, assetId } = useAssetParams();
    const { isDesktop } = useResponsive();
    const [openDropdown, toggleDropdown] = useReducer((prev) => !prev, false);
    const { data, isError, error } = useAsset(assetType, assetId);
    const activeModal = useAssetActionModalStore((state) => state.activeModal);
    const setActiveModal = useAssetActionModalStore((state) => state.setActiveModal);

    if (isError && !data) {
        return <QueryError error={error} />;
    }
    if (!data) {
        return null;
    }

    const orderType = selectOrderType(data);
    const buttonsConfig = getComponentConfig(ASSET_ACTIONS_CONFIG, assetType, orderType);

    if (!buttonsConfig) {
        return null;
    }

    return (
        <>
            <QueryFetchingIndicator source="asset_details" />
            {isDesktop &&
                buttonsConfig.map((key) => {
                    const ActionComponent = ACTION_BUTTONS_CONFIG[key];
                    if (!ActionComponent) {
                        return null;
                    }

                    return <ActionComponent key={key} type="button" />;
                })}

            <AssetToggleDropdownButton toggleDropdown={toggleDropdown} isOpen={openDropdown} />
            <AssetCloseButton />

            {isDesktop ? (
                <AssetContextDropdown onClose={toggleDropdown} visible={openDropdown} />
            ) : (
                <AssetContextDropdownModal visible={openDropdown} onClose={toggleDropdown} />
            )}

            {openDropdown && (
                <div
                    className={styles.ellipses_overlay}
                    onClick={toggleDropdown}
                    onKeyDown={(event) => {
                        isActionKey(event.key) && toggleDropdown();
                    }}
                    // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role
                    role="button"
                    tabIndex={0}
                    aria-label={translate('order_status_header_close_dropdown_label')}
                />
            )}

            {/*
             * Render Modal Components
             * This section renders modal components for each configured asset action.
             * It checks if a modal configuration exists for each button in the configuration,
             * and renders the corresponding modal when its trigger is active.
             */}
            {buttonsConfig.map((key) => {
                // Skip rendering if no modal configuration exists for this action, eg: orderHistory action
                if (!ASSET_ACTION_MODALS_CONFIG[key]) {
                    return null;
                }

                // Retrieve the specific modal component for this asset action
                const ModalComponent: React.ComponentType<ActionModalProps> = ASSET_ACTION_MODALS_CONFIG[key];

                return (
                    <ModalComponent
                        key={`modal-${key}`}
                        visible={activeModal === key}
                        onClose={() => {
                            setActiveModal(null);
                        }}
                    />
                );
            })}
        </>
    );
};
